/**
 * Companion Talk attachments — same UX as vde76 SmartChat:
 * stage images first, then send with the message. Ctrl+V screenshots supported.
 */

const MAX_PENDING_IMAGES = 4;
const MAX_IMAGE_SIDE = 1280;
const JPEG_QUALITY = 0.72;

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Compress data-URL before POST (body-limit / nginx). */
export async function compressImageDataUrl(dataUrl, maxSide = MAX_IMAGE_SIDE, quality = JPEG_QUALITY) {
  if (typeof window === 'undefined' || !String(dataUrl || '').startsWith('data:image/')) {
    return dataUrl;
  }
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = dataUrl;
    });
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    if (scale >= 1 && dataUrl.length < 400_000) return dataUrl;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    return dataUrl;
  }
}

export async function filesToPendingImages(files, { max = MAX_PENDING_IMAGES } = {}) {
  const list = Array.from(files || []).filter((f) => f && String(f.type || '').startsWith('image/'));
  if (!list.length) return [];
  const encoded = await Promise.all(list.map(async (file) => {
    const raw = await fileToDataUrl(file);
    return compressImageDataUrl(raw);
  }));
  return encoded.filter(Boolean).slice(0, max);
}

/** Extract image files from a paste/clipboard event. */
export function imageFilesFromClipboard(clipboardData) {
  const items = clipboardData?.items;
  if (!items?.length) return [];
  const files = [];
  for (const item of Array.from(items)) {
    if (item.type && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files;
}

export { MAX_PENDING_IMAGES };
